import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { useBrand } from "@/contexts/BrandContext";
import { hasActiveVoiceAccount } from "@/hooks/useVoiceCredits";
import { parseVoiceCreditSummary, type VoiceCreditSummary } from "@/lib/voice-credits";

export interface TopupWallets {
  chatBalanceCop: number | null;
  voice: VoiceCreditSummary | null;
  voiceAvailable: boolean;
  loaded: boolean;
}

async function readChatBalance(orgId: string): Promise<number | null> {
  try {
    const { data, error } = await supabase
      .from("credit_balances" as never)
      .select("balance_cop" as never)
      .eq("organization_id" as never, orgId as never)
      .maybeSingle();
    if (error) return null;
    return Number((data as { balance_cop?: number } | null)?.balance_cop ?? 0);
  } catch {
    return null;
  }
}

async function readVoice(orgId: string): Promise<VoiceCreditSummary | null> {
  try {
    if (!(await hasActiveVoiceAccount(orgId))) return null;
    const { data, error } = await supabase.rpc("voice_credit_summary" as never, { p_org: orgId } as never);
    return error ? null : parseVoiceCreditSummary(data);
  } catch {
    return null;
  }
}

export function useTopupWallets(orgId: string | null, enabled: boolean): TopupWallets {
  const brand = useBrand();
  const voiceAllowed = brand.id !== "etb" && brand.voiceAssistants;
  const [state, setState] = useState<TopupWallets>({ chatBalanceCop: null, voice: null, voiceAvailable: false, loaded: false });

  useEffect(() => {
    if (!orgId || !enabled) return;
    let alive = true;
    void Promise.all([readChatBalance(orgId), voiceAllowed ? readVoice(orgId) : Promise.resolve(null)]).then(([chat, voice]) => {
      if (!alive) return;
      setState({
        chatBalanceCop: chat,
        voice,
        voiceAvailable: !!voice && !voice.exempt && !!voice.priceCop,
        loaded: true,
      });
    });
    return () => { alive = false; };
  }, [orgId, enabled, voiceAllowed]);

  return state;
}
